import Api from '@/util/Api';
import type { ApiResponse } from '@/types/api';

/**
 * `GET v2.0/Calendar/availability` — the solver behind legacy `View/js/cart-gcal.js` — read for ONE
 * week, for one team member or (ROBO-694) for a team, and the pure helpers that turn its answer
 * into what a picker draws.
 *
 * No React here: the hook (`use-availability.ts`) and the component (`availability-picker.tsx`)
 * sit on top, and the public cart migration and ROBO-694 (choosing a photographer) are expected to
 * reuse this file as it is.
 *
 * WHAT IS REPLICATED FROM `cart-gcal.js`, with the line it comes from:
 * - the unavailable answer (`{"error":"true","availability_messages":{…}}`) shows the member's own
 *   message when there is one, else "Please call to schedule." (`errorWeeks`, :432-452);
 * - `late == -1` is a date-only calendar: a free day is offered whole, with no times (:228-232);
 * - today is never bookable, and neither are the `daysout - 1` days after it (:223-226);
 * - own hold ids go back as `sIgnoreRes`, dashed, so a caller's hold does not hide its own slot
 *   (:57-58, :513).
 *
 * TEAM MODE (ROBO-694), also from `cart-gcal.js`:
 * - several `nUserID`s with `nTeam` 1 or 2 ask for ONE merged calendar (`loadWeeks` :515); each
 *   slot then names who can take it — `"<minute>:<uid>/<rank>:<uid>/<rank>"` (`slotClick`
 *   :797-803) — and the answer's `open` map carries each member's open-order count (:213);
 * - `sWeekMask` (`id:mask,…`, :895-901) and `nFavorite` (comma-joined ids, :509) ride along;
 * - WHICH of a slot's members gets the appointment is `slot-resolver.ts` (`slotClick` :805-854).
 *   This file only reports the candidates.
 * A request with no `teamMemberIds` is the single-member read it always was, byte for byte.
 *
 * WHAT IS DELIBERATELY NOT HERE (the cart migration): the combined calendar (:65-79) and the
 * "Will any of these appointments work?" best-appointment shortcut (`best_appt`, :286-319).
 */

export const AVAILABILITY_PATH = 'v2.0/Calendar/availability';

/** Legacy's fallback when the solver has no answer and the member has no message of their own. */
export const CALL_TO_SCHEDULE = 'Please call to schedule.';

export interface AvailabilityLocation {
    latitude: number;
    longitude: number;
}

/** Design var `cart-gcal.team`: 0 = one member's own calendar, 1 / 2 = a merged team calendar. */
export type AvailabilityTeamMode = 0 | 1 | 2;

/** One member who can take a team-calendar slot. `rank` 1 is the best; 0 means "not ranked". */
export interface SlotCandidate {
    id: number;
    rank: number;
}

/** One team-calendar slot: its start minute and who can take it. */
export interface AvailabilitySlot {
    minute: number;
    candidates: SlotCandidate[];
}

export interface AvailabilityRequest {
    brandId: number;
    /** The one member whose calendar is read. Ignored when `teamMemberIds` asks for a team. */
    teamMemberId?: number;
    /**
     * TEAM MODE: every member the merged calendar may draw on. Used only together with `team`
     * 1 or 2; otherwise the request is the single-member one.
     */
    teamMemberIds?: readonly number[];
    /** `nTeam`. 0 / absent = single member. */
    team?: AvailabilityTeamMode;
    /** `sWeekMask`, already in wire form (`id:mask,id:mask`) — see `buildWeekMask`. */
    weekMask?: string;
    /** `nFavorite`: the shopper's favourite members. Sent in team mode only. */
    favoriteIds?: readonly number[];
    /** Minutes on site; `-1` is a twilight / date-only product. */
    duration: number;
    /** Any day of the wanted week, `YYYY-MM-DD`. The API snaps it to the Sunday on or before. */
    weekStart: string;
    location?: AvailabilityLocation | null;
    /** Tasks being moved, so a time overlapping the old appointment can still be offered. */
    ignoreTaskIds?: readonly number[];
    /** The caller's own hold ids (`use-slot-hold`), so its hold does not hide its own slot. */
    ignoreReservationIds?: readonly string[];
    /**
     * A site's default download guid. Set ONLY for a guest: the call then goes out with no session
     * and `hd-download-guid`, which is what lets the API honour `ignoreTaskIds` for them.
     */
    downloadGuid?: string;
}

export interface AvailabilityDay {
    /** `YYYY-MM-DD`. */
    date: string;
    /** Start times as minutes after midnight, ascending. Empty on a date-only calendar. */
    slots: number[];
    /** Date-only calendars: whether the day can be picked whole. */
    isOpenDay: boolean;
    /**
     * TEAM MODE only: the same slots with who can take each. Absent on a single-member read. On a
     * date-only calendar the entries are kept as sent (their minute is not a time of day).
     */
    teamSlots?: AvailabilitySlot[];
}

export type AvailabilityWeek =
    | { kind: 'unavailable'; message: string }
    | {
          kind: 'open';
          /** The Sunday the API anchored the week on. */
          weekStart: string;
          /** `late == -1`: days are offered whole, with no start times. */
          isDateOnly: boolean;
          /** The route's `daysout`: book at least this many days ahead. */
          daysOut: number;
          days: AvailabilityDay[];
          /**
           * TEAM MODE only: the answer's `open` map — open-order count per member id, which
           * `teamselect` 2 ("fewest open appointments") resolves a slot with.
           */
          openOrders?: Record<number, number>;
      };

/** The un-enveloped body. `Api.response` spreads it onto the response, so these are top-level. */
interface AvailabilityBody {
    appts?: unknown;
    base_date?: unknown;
    late?: unknown;
    mode?: unknown;
    daysout?: unknown;
    availability_messages?: unknown;
    open?: unknown;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function pad(value: number): string {
    return String(value).padStart(2, '0');
}

/** A calendar date as a UTC timestamp, so day arithmetic never crosses a DST change. */
function toUtc(date: string): number {
    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
}

function fromUtc(time: number): string {
    const date = new Date(time);
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function addDays(date: string, days: number): string {
    return fromUtc(toUtc(date) + days * DAY_MS);
}

/** 0 = Sunday. */
export function dayOfWeek(date: string): number {
    return new Date(toUtc(date)).getUTCDay();
}

/** The Sunday on or before `date` — the same snap the API applies to `sStart`. */
export function weekStartOf(date: string): string {
    return addDays(date, -dayOfWeek(date));
}

/** The browser's own calendar date. A picker host that knows the brand's clock passes its own. */
export function localToday(): string {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `base_date` arrives as `M/D/YYYY` (the route's own format); an ISO date is accepted too. */
export function parseApiDate(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(value);
    if (us) return `${us[3]}-${pad(Number(us[1]))}-${pad(Number(us[2]))}`;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : undefined;
}

/**
 * One `appts` entry. A single-member answer is a bare number (no candidates); a team-mode one is
 * `"<minute>:<uid>/<rank>:<uid>/<rank>"` — members joined with COLONS, each with an optional
 * `/rank` (`cart-gcal.js` :797-803; a member with no score has no suffix, which reads as rank 0).
 */
export function parseSlotEntry(entry: unknown): AvailabilitySlot | undefined {
    const [first, ...members] = String(entry).split(':');
    const minute = parseInt(first, 10);
    if (!Number.isFinite(minute)) return undefined;
    const candidates: SlotCandidate[] = [];
    for (const member of members) {
        const [idText, rankText] = member.split('/');
        const id = parseInt(idText, 10);
        if (!Number.isFinite(id) || id <= 0) continue;
        const rank = parseInt(rankText ?? '', 10);
        candidates.push({ id, rank: Number.isFinite(rank) ? rank : 0 });
    }
    return { minute, candidates };
}

/** Whether a request asks for a merged team calendar rather than one member's own. */
export function isTeamRequest(
    request: Pick<AvailabilityRequest, 'team' | 'teamMemberIds'>
): boolean {
    return (request.team === 1 || request.team === 2) && (request.teamMemberIds?.length ?? 0) > 0;
}

/**
 * `sWeekMask` from service-area data: `id:mask` for every member with a non-zero mask, comma
 * joined (`cart-gcal.js` :895-901). Returns `undefined` when nobody has one, so nothing is sent.
 */
export function buildWeekMask(
    members: readonly { id: number; weekMask?: number | null }[]
): string | undefined {
    const parts = members
        .filter(member => Boolean(member.weekMask))
        .map(member => `${member.id}:${member.weekMask}`);
    return parts.length > 0 ? parts.join(',') : undefined;
}

function parseOpenOrders(value: unknown): Record<number, number> {
    const open: Record<number, number> = {};
    if (value && typeof value === 'object') {
        for (const [id, count] of Object.entries(value as Record<string, unknown>)) {
            const key = Number(id);
            const number = Number(count);
            if (Number.isFinite(key) && Number.isFinite(number)) open[key] = number;
        }
    }
    return open;
}

/** `9:00 AM` for 540. The times are the brand's own wall clock; nothing is converted. */
export function formatSlotTime(minute: number): string {
    const hours = Math.floor(minute / 60);
    const hour12 = ((hours + 11) % 12) + 1;
    return `${hour12}:${pad(minute % 60)} ${hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * Whether legacy would offer `date` at all: never today, and not inside the `daysOut` notice
 * (`cart-gcal.js` :223-226 — `os == tos` is "Today", slots start at `os > tos + daysout - 1`).
 */
export function isBookableDate(date: string, today: string, daysOut: number): boolean {
    return toUtc(date) >= toUtc(addDays(today, Math.max(daysOut, 1)));
}

function memberMessage(body: AvailabilityBody, teamMemberId: number | undefined): string {
    const messages = body.availability_messages;
    if (teamMemberId !== undefined && messages && typeof messages === 'object') {
        const own = (messages as Record<string, unknown>)[String(teamMemberId)];
        if (typeof own === 'string' && own.trim().length > 0) return own;
    }
    return CALL_TO_SCHEDULE;
}

/**
 * ★ `appts` BEING AN ARRAY IS THE TEST FOR "OPEN", NOT `error`. `Api.response` writes its own
 * `error` key after spreading the body, so the body's `"error":"true"` is gone by the time it gets
 * here (the same trap `hubby-core/tools/team-availability-tools.ts` documents).
 */
export function parseAvailability(
    response: ApiResponse<unknown>,
    request: Pick<AvailabilityRequest, 'teamMemberId' | 'weekStart'> &
        Partial<Pick<AvailabilityRequest, 'team' | 'teamMemberIds'>>
): AvailabilityWeek {
    const body = response as unknown as AvailabilityBody;
    const isTeam = isTeamRequest(request);
    if (!Array.isArray(body.appts)) {
        // A team has no one member whose message to show: legacy's "Please call to schedule."
        return {
            kind: 'unavailable',
            message: memberMessage(body, isTeam ? undefined : request.teamMemberId)
        };
    }

    const weekStart = parseApiDate(body.base_date) ?? weekStartOf(request.weekStart);
    const isDateOnly = Number(body.late) === -1;
    const daysOut = Number.parseInt(String(body.daysout ?? '0'), 10) || 0;

    const days = (body.appts as unknown[]).slice(0, 7).map((entries, index): AvailabilityDay => {
        const parsed = (Array.isArray(entries) ? entries : [])
            .map(parseSlotEntry)
            .filter((slot): slot is AvailabilitySlot => slot !== undefined);
        const minutes = parsed.map(slot => slot.minute);
        const day: AvailabilityDay = {
            date: addDays(weekStart, index),
            slots: isDateOnly ? [] : minutes.filter(minute => minute >= 0).sort((a, b) => a - b),
            isOpenDay: isDateOnly && minutes.length > 0
        };
        if (isTeam) {
            day.teamSlots = isDateOnly
                ? parsed
                : parsed.filter(slot => slot.minute >= 0).sort((a, b) => a.minute - b.minute);
        }
        return day;
    });

    return {
        kind: 'open',
        weekStart,
        isDateOnly,
        daysOut,
        days,
        ...(isTeam ? { openOrders: parseOpenOrders(body.open) } : {})
    };
}

/** A hold id (32 hex digits) as the dashed guid `sIgnoreRes` binds to; anything else is dropped. */
function toGuid(id: string): string | undefined {
    const hex = id.replace(/[^0-9a-fA-F]/g, '');
    return hex.length === 32
        ? `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
        : undefined;
}

export function buildAvailabilityQuery(request: AvailabilityRequest): string {
    const params = new URLSearchParams();
    const isTeam = isTeamRequest(request);
    params.set('nBrandID', String(request.brandId));
    if (isTeam) {
        for (const id of request.teamMemberIds ?? []) params.append('nUserID', String(id));
    } else {
        params.set('nUserID', String(request.teamMemberId));
    }
    params.set('nDuration', String(request.duration));
    // 0 = the member's own calendar mode; 1 / 2 = the merged team calendar (`cart-gcal.team`).
    params.set('nTeam', isTeam ? String(request.team) : '0');
    params.set('nWeeks', '1');
    params.set('sStart', weekStartOf(request.weekStart));
    // Legacy sends the masks for a single member's calendar too (:895-901, :515).
    if (request.weekMask) params.set('sWeekMask', request.weekMask);
    if (isTeam && request.favoriteIds && request.favoriteIds.length > 0) {
        params.set('nFavorite', request.favoriteIds.join(','));
    }
    if (request.location) {
        params.set('sLocation', `${request.location.latitude},${request.location.longitude}`);
    }
    for (const taskId of request.ignoreTaskIds ?? []) {
        params.append('nIgnoreTaskID', String(taskId));
    }
    for (const id of request.ignoreReservationIds ?? []) {
        const guid = toGuid(id);
        if (guid) params.append('sIgnoreRes', guid);
    }
    params.set('nReturnAvailMsgs', 'true');
    return params.toString();
}

/**
 * The headers every availability call carries: the brand host (ROBO-581 — the API resolves the
 * brand from it rather than trusting a bare `nBrandID`), plus the guid for a guest.
 */
export function availabilityHeaders(downloadGuid?: string): Record<string, string> {
    return {
        'hd-brand-host': window.location.hostname,
        ...(downloadGuid ? { 'hd-download-guid': downloadGuid } : {})
    };
}

/**
 * One week for one member, or for a team. Throws when the request itself fails; an "unavailable" answer is a
 * successful read and comes back as `kind: 'unavailable'`.
 *
 * A guest (`downloadGuid` set) is sent with `skipAuth`, so `Api` never looks for a session; anyone
 * else goes out with their bearer token and no guid.
 */
export async function fetchAvailability(request: AvailabilityRequest): Promise<AvailabilityWeek> {
    const isGuest = Boolean(request.downloadGuid);
    const response = await Api.get<undefined, unknown>(
        `${AVAILABILITY_PATH}?${buildAvailabilityQuery(request)}`,
        undefined,
        isGuest,
        { headers: availabilityHeaders(request.downloadGuid) }
    );
    if (!response.ok) {
        throw response.error ?? new Error(response.statusText || 'Unable to load availability');
    }
    return parseAvailability(response, request);
}
